import * as React from 'react';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
  IconButton, Menu, MenuItem, Tooltip, Typography, Divider, Skeleton,
  Button,
  ListItemButton
} from '@mui/material';
import { Logout, Person, Menu as MenuIcon, ChevronLeft } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks/useAppDispatch';
import { useAppSelector } from '../../store/hooks/useAppSelector';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/types/rootState';
import { logoutAction } from '../../store/slices/auth';
import { saveUserAction } from '../../store/slices/user';
import { useGetAccountQuery, useGetRafflesQuery } from '../../store/api';
import { saveAccountAction } from '../../store/slices/account';
import { saveSelectedRaffleAction } from '../../store/slices/selectedRaffle';
import { PAGES } from '../../constants/pages';
import { RaffleSelector } from '../raffle/utils/raffleSelector';
import { useIsMobileView } from '../../utils/responsive';
import { IRaffle } from '../../store/interfaces/IRaffle';
import { getPrimaryColorByAccountId } from '../../utils/styleConstants';

const drawerWidth = 220;
const collapsedWidth = 62;

export default function SidebarHeader() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoggedIn = useSelector(({ authentication }: RootState) => authentication.isLoggedIn);
  const profile = useSelector(({ user }: RootState) => user.profile);
  const accountInformation = useAppSelector(({ account }: RootState) => account);
  const isMobile = useIsMobileView();
  const acccountColor = getPrimaryColorByAccountId(accountInformation.id);

  const { data: accountFounded, error: errorGetAccount, isUninitialized, isLoading, isError } = useGetAccountQuery(null, {});

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);  
  const [collapsed, setCollapsed] = React.useState(true);

  React.useEffect(() => {
    if (!errorGetAccount || isError) {
      dispatch(saveAccountAction(accountFounded));
    }
  }, [accountFounded, dispatch, errorGetAccount, isError]);

  const handleNavigationTo = (componentUrl: string) => {
    navigate(componentUrl);
  };

  const handleLogout = () => {
    dispatch(logoutAction({ isLoggedIn: false, token: '' }));
    dispatch(saveUserAction(null));
    navigate('/login');
  };

  const filteredPages = PAGES.filter(page =>
    page.availableProfiles.includes(profile) &&
    (accountInformation.owner ? true : !page.ownerFuncionality) &&
    ( page.name !== 'Trabajo pendiente' || ['agudojhonny', 'oscar-alarcon-promo', 'minas-de-baruta', 'el-del-puerto-rifas', 'super-rifas-latam'].includes(accountInformation.id))
    && !(page.name === 'Verificacion' && accountInformation.id === 'oscar-alarcon-promo')
  );

  const skeletonSidebar = (
    <Box sx={{ width: drawerWidth, padding: 2 }}>
      {[1, 2, 3].map((_, i) => (
        <Skeleton key={i} animation="wave" height={40} sx={{ mb: 2 }} />
      ))}
    </Box>
  );

  if (isUninitialized || isLoading) return skeletonSidebar;
  if (isError || !accountFounded) return <></>;
  if (!isLoggedIn) return <></>;
  if (isMobile) return <></>;

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: collapsed ? collapsedWidth : drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: collapsed ? collapsedWidth : drawerWidth,
          boxSizing: 'border-box',
          transition: 'width 0.3s',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Header + Collapse Button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: !collapsed ? 1 : 0,
          py: 1.5,
          minHeight: 84,
          backgroundColor: 'background.paper',
        }}
      >
        <Button onClick={() => setCollapsed(!collapsed)} sx={{ p: 0, m: 0 }}>
          <img
            src={accountInformation.logo}
            alt="logo"
            width={40}
            height={40}
            style={{ marginRight: collapsed ? 0 : 12, borderRadius: '10px' }}
          />
          {!collapsed && (
            <Typography variant="h6" noWrap fontSize={16}>
              {accountInformation.name}
            </Typography>
          )}
        </Button>

        <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
          {collapsed ? <MenuIcon /> : <ChevronLeft />}
        </IconButton>
      </Box>

      <Divider />

      {/* Menu */}
      <List>
        {filteredPages.map((page) => (
          <ListItemButton key={page.name} onClick={() => handleNavigationTo(page.url)}>
            <Tooltip title={collapsed ? page.name : ''} placement="right">
              <ListItemIcon sx={{ paddingLeft: '2px', color: acccountColor  }}>{page.icon}</ListItemIcon>
            </Tooltip>
            {!collapsed && <ListItemText primary={page.name} />}
          </ListItemButton>
        ))}
      </List>

      {/* Footer */}
      <Box mt="auto" px={1}>
        <Divider sx={{ my: 2 }} />
        <MenuItem onClick={handleLogout}>
            <ListItemIcon sx={{ color: acccountColor }}><Logout fontSize="small" /></ListItemIcon>
            Cerrar sesión
          </MenuItem>
      </Box>
    </Drawer>
  );
}
